// File: src/components/admin/scheduler/UserSchedulerDetailDialog.js
// Full detail dialog for viewing/managing a specific user's schedules

import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Chip,
    Divider,
    ListItemIcon,
    CircularProgress,
    Alert,
    Switch,
    Menu,
    MenuItem,
    Link,
} from '@mui/material';
import {
    PlayArrow as TriggerIcon,
    Pause as PauseIcon,
    PlayCircle as ResumeIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Schedule as ScheduleIcon,
    RestartAlt as ResetIcon,
    HealthAndSafety as HealthIcon,
    Build as RepairIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';
import { formatRelativeTime } from '../../../utils/scheduleUtils';
import CreateScheduleDialog from './CreateScheduleDialog';

const UserSchedulerDetailDialog = ({ open, onClose, userUid, onRefresh }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [healthResult, setHealthResult] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [userEmail, setUserEmail] = useState(null);

    const handleHealthCheck = async (repair = false) => {
        setHealthLoading(true);
        setHealthResult(null);
        try {
            const result = await adminApi.healthCheckUser(userUid, { repair });
            setHealthResult(result);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setHealthResult({ success: false, status: 'error', issue: message });
        } finally {
            setHealthLoading(false);
        }
    };

    const fetchData = useCallback(async () => {
        if (!userUid) return;

        setLoading(true);
        setError(null);

        try {
            const result = await adminApi.getSchedulerUserDetail(userUid);
            setData(result);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [userUid]);

    useEffect(() => {
        if (open && userUid) {
            fetchData();
            setHealthResult(null);
            setUserEmail(null);
            adminApi.getUser(userUid).then(user => {
                setUserEmail(user?.email || null);
            }).catch(() => {});
        }
    }, [open, userUid, fetchData]);

    const handleAction = async (action, scheduleId = null) => {
        setActionLoading(action + (scheduleId || ''));
        setError(null);

        try {
            switch (action) {
                case 'trigger':
                    await adminApi.triggerUserSchedule(userUid, scheduleId);
                    break;
                case 'pause':
                    await adminApi.pauseUserSchedules(userUid);
                    break;
                case 'resume':
                    await adminApi.resumeUserSchedules(userUid);
                    break;
                case 'delete':
                    await adminApi.deleteUserSchedule(userUid, scheduleId);
                    break;
                case 'toggle':
                    const schedule = data.schedules.find(s => s.id === scheduleId);
                    await adminApi.updateUserSchedule(userUid, scheduleId, {
                        enabled: !schedule.enabled,
                    });
                    break;
                case 'reset':
                    await adminApi.resetScheduleCircuitBreaker(userUid, scheduleId);
                    break;
                default:
                    break;
            }

            // Refresh data after action
            await fetchData();
            if (onRefresh) onRefresh();
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(message);
        } finally {
            setActionLoading(null);
            setMenuAnchor(null);
        }
    };

    const handleCreateSchedule = async (formData) => {
        setActionLoading('create');
        try {
            await adminApi.createUserSchedule(userUid, formData);
            setCreateDialogOpen(false);
            await fetchData();
            if (onRefresh) onRefresh();
        } catch (err) {
            throw err;
        } finally {
            setActionLoading(null);
        }
    };

    const openMenu = (event, schedule) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setSelectedSchedule(schedule);
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '-';
        return new Date(ts).toLocaleString();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'COMPLETED':
                return <SuccessIcon fontSize="small" color="success" />;
            case 'FAILED':
                return <ErrorIcon fontSize="small" color="error" />;
            default:
                return <ScheduleIcon fontSize="small" color="action" />;
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="scheduler-detail-dialog">
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Scheduler Details</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchData} disabled={loading}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        User:{' '}
                        <Link component={RouterLink} to={`/users/${userUid}`} underline="hover">
                            {userEmail || userUid}
                        </Link>
                        {userEmail && (
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                ({userUid})
                            </Typography>
                        )}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : data ? (
                        <>
                            {/* Stats Summary */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }} data-testid="scheduler-detail-stats">
                                <Chip
                                    label={`${data.stats.totalCount} schedules`}
                                    variant="outlined"
                                    data-testid="scheduler-detail-total-count"
                                />
                                <Chip
                                    label={`${data.stats.enabledCount} enabled`}
                                    color="success"
                                    data-testid="scheduler-detail-enabled-count"
                                    variant={data.stats.enabledCount > 0 ? 'filled' : 'outlined'}
                                />
                                {data.stats.pausedCount > 0 && (
                                    <Chip
                                        label={`${data.stats.pausedCount} paused`}
                                        color="warning"
                                    />
                                )}
                                {data.stats.failedCount > 0 && (
                                    <Chip
                                        icon={<WarningIcon />}
                                        label={`${data.stats.failedCount} circuit breaker`}
                                        color="error"
                                    />
                                )}
                                {data.stats.nextRun && (
                                    <Chip
                                        icon={<ScheduleIcon />}
                                        label={`Next: ${formatRelativeTime(data.stats.nextRun)}`}
                                    />
                                )}
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateDialogOpen(true)}
                                >
                                    Add Schedule
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<PauseIcon />}
                                    onClick={() => handleAction('pause')}
                                    disabled={actionLoading || data.stats.enabledCount === 0}
                                >
                                    Pause All
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<ResumeIcon />}
                                    onClick={() => handleAction('resume')}
                                    disabled={actionLoading || data.stats.pausedCount === 0}
                                >
                                    Resume All
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<HealthIcon />}
                                    onClick={() => handleHealthCheck(false)}
                                    disabled={healthLoading}
                                >
                                    Health Check
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<RepairIcon />}
                                    onClick={() => handleHealthCheck(true)}
                                    disabled={healthLoading}
                                >
                                    Wake & Repair
                                </Button>
                            </Box>

                            {/* Health Check Result */}
                            {healthResult && (
                                <Alert
                                    severity={
                                        healthResult.status === 'healthy' || healthResult.status === 'idle'
                                            ? 'success'
                                            : healthResult.repaired
                                                ? 'info'
                                                : 'warning'
                                    }
                                    onClose={() => setHealthResult(null)}
                                    sx={{ mb: 2 }}
                                >
                                    <strong>Status: {healthResult.status}</strong>
                                    {healthResult.issue && ` — ${healthResult.issue}`}
                                    {healthResult.repaired && ' (repaired)'}
                                    {healthResult.activeSchedules != null && (
                                        <Typography variant="caption" display="block">
                                            {healthResult.activeSchedules} active / {healthResult.totalSchedules} total schedules
                                            {healthResult.alarmTime && ` • Alarm: ${new Date(healthResult.alarmTime).toLocaleString()}`}
                                        </Typography>
                                    )}
                                </Alert>
                            )}

                            {/* Schedules Table */}
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Schedules
                            </Typography>
                            <TableContainer sx={{ mb: 3 }} data-testid="scheduler-detail-schedules-table">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Platform</TableCell>
                                            <TableCell>Interval</TableCell>
                                            <TableCell align="center">Enabled</TableCell>
                                            <TableCell>Last Run</TableCell>
                                            <TableCell>Next Run</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.schedules.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Typography color="text.secondary">
                                                        No schedules configured
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            data.schedules.map((schedule) => (
                                                <TableRow key={schedule.id} data-testid={`scheduler-schedule-row-${schedule.id}`}>
                                                    <TableCell>
                                                        <Box data-testid={`scheduler-schedule-type-${schedule.id}`}>
                                                            <Typography variant="body2">
                                                                {schedule.type}
                                                            </Typography>
                                                            {schedule.type === 'AI_ANALYSIS' && schedule.config?.taskId && (
                                                                <Typography variant="caption" color="text.secondary" data-testid={`scheduler-schedule-task-id-${schedule.id}`}>
                                                                    Task: {schedule.config.taskId}
                                                                </Typography>
                                                            )}
                                                            {schedule.disabledReason === 'circuit_breaker' && (
                                                                <Chip
                                                                    label="Circuit Breaker"
                                                                    size="small"
                                                                    color="error"
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{schedule.platformId || '-'}</TableCell>
                                                    <TableCell data-testid={`scheduler-schedule-interval-${schedule.id}`}>{schedule.interval}</TableCell>
                                                    <TableCell align="center">
                                                        <Switch
                                                            data-testid={`scheduler-schedule-enabled-${schedule.id}`}
                                                            size="small"
                                                            checked={schedule.enabled}
                                                            disabled={actionLoading || schedule.disabledReason === 'circuit_breaker'}
                                                            onChange={() => handleAction('toggle', schedule.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatRelativeTime(schedule.lastRun)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {schedule.enabled && !schedule.paused
                                                            ? formatRelativeTime(schedule.nextRun)
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Trigger Now">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleAction('trigger', schedule.id)}
                                                                disabled={actionLoading === `trigger${schedule.id}`}
                                                            >
                                                                {actionLoading === `trigger${schedule.id}` ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <TriggerIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => openMenu(e, schedule)}
                                                        >
                                                            <MoreIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Recent History */}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Recent Execution History
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {data.recentHistory.length === 0 ? (
                                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                        No execution history
                                    </Typography>
                                ) : (
                                    data.recentHistory.map((entry, index) => {
                                        const errorMsg = entry.error || entry.summary?.details;
                                        const isFailed = entry.status === 'FAILED' || entry.status === 'AUTH_EXPIRED';
                                        return (
                                            <Box
                                                key={entry.id || index}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: isFailed ? 'error.light' : 'divider',
                                                    bgcolor: isFailed ? 'error.50' : 'transparent',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    {getStatusIcon(entry.status)}
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {entry.scheduleType}
                                                    </Typography>
                                                    <Chip
                                                        label={entry.status}
                                                        size="small"
                                                        color={entry.status === 'COMPLETED' ? 'success' : entry.status === 'SKIPPED' ? 'default' : 'error'}
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                                        {formatTimestamp(entry.completedAt)}
                                                        {entry.duration != null && ` • ${(entry.duration / 1000).toFixed(1)}s`}
                                                    </Typography>
                                                </Box>
                                                {isFailed && errorMsg && (
                                                    <Alert severity="error" variant="outlined" sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                                        {errorMsg}
                                                    </Alert>
                                                )}
                                                {!isFailed && entry.summary?.details && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 3.5 }}>
                                                        {entry.summary.details}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </>
                    ) : null}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Context Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
            >
                {selectedSchedule?.disabledReason === 'circuit_breaker' && (
                    <MenuItem onClick={() => handleAction('reset', selectedSchedule?.id)}>
                        <ListItemIcon><ResetIcon fontSize="small" /></ListItemIcon>
                        Reset Circuit Breaker
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => handleAction('delete', selectedSchedule?.id)}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    Delete Schedule
                </MenuItem>
            </Menu>

            {/* Create Schedule Dialog */}
            <CreateScheduleDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSubmit={handleCreateSchedule}
                loading={actionLoading === 'create'}
                userUid={userUid}
            />
        </>
    );
};

export default UserSchedulerDetailDialog;
